import { isNumeric } from '../../util/helpers';
import { parseLatLon } from '../../airport/util/parseUtils';
import { BusinessFinderResult } from './business-finder-result.model';

/**
 * Category labels for the results card, in display order, with the tag prefixes that map to each. Mirrors the
 * iOS ServiceBadge groupings (Plane/Rotorcraft/Equipment Rental, Sales). `Sales::Types::*` is a modifier
 * (New/Used/Consignment), not a category, and `Rental::Private` marks rentals NOT open to the public — both
 * are excluded here.
 */
export const RENTAL_SALES_CATEGORY_PREFIXES: { label: string; prefixes: string[] }[] = [
  { label: 'Aircraft Rental', prefixes: ['Rental::Airplane::', 'Rental::Glider', 'Rental::Rotorcraft::'] },
  { label: 'Simulator Rental', prefixes: ['Rental::Simulator::'] },
  { label: 'Equipment Rental', prefixes: ['Rental::Equipment::'] },
  { label: 'Aircraft Sales', prefixes: ['Sales::Aircraft::'] },
  { label: 'Avionics Sales', prefixes: ['Sales::Avionics::'] }
];

/**
 * Tag name marking a business whose rentals are NOT available to the general public (e.g. members only).
 * A business carrying ONLY this rental/sales tag is excluded from the finder server-side; one carrying it
 * alongside other rental/sales tags is included and disclosed via {@link RentalSalesResult.privateRentals}.
 */
export const RENTAL_PRIVATE_TAG = 'Rental::Private';

/**
 * A Rentals/Sales search result returned by the AirportsAPI `~/rental-sales` endpoint. A rentals/sales
 * business is one carrying any `Rental::` or `Sales::` tag other than `Rental::Private` — unlike the other
 * finders it spans business types (FBOs, flight schools, avionics shops, ...). Mirrors
 * {@link ServiceRepairResult}'s `static parse` convention.
 */
export class RentalSalesResult extends BusinessFinderResult {

  /**
   * Thumbnail image for the results card (business logo or first photo). Null when it has no photo.
   */
  thumbnailPhotoPath: string;

  /**
   * True when the business's rentals are not available to the general public (Rental::Private) — shown as a
   * caveat on the results card, mirroring the iOS "Rentals not available to the general public" disclosure.
   */
  privateRentals: boolean;

  /**
   * Distinct rental/sales categories this business covers (e.g. ["Aircraft Rental", "Avionics Sales"]),
   * derived from its tags and shown on the results card. Ordered per {@link RENTAL_SALES_CATEGORY_PREFIXES}.
   */
  categories: string[];

  static parse(data: any): RentalSalesResult {
    const tags: string[] = (data.tags || []).map(d => d);
    const categories = RENTAL_SALES_CATEGORY_PREFIXES
      .filter(c => tags.some(t => c.prefixes.some(p => t.indexOf(p) === 0)))
      .map(c => c.label);
    return Object.assign(new RentalSalesResult(), {
      businessId: data.businessId,
      name: data.name || null,
      city: data.city || null,
      stateProvince: data.stateProvince || null,
      latitude: isNumeric(data.latitude) ? +data.latitude : null,
      longitude: isNumeric(data.longitude) ? +data.longitude : null,
      location: (isNumeric(data.latitude) && isNumeric(data.longitude))
        ? parseLatLon(data.latitude, data.longitude) : null,
      phone1: data.phone1 || null,
      webAddress: data.webAddress || null,
      emailAddress: data.emailAddress || null,
      description: data.description || null,
      distance: isNumeric(data.distance) ? +data.distance : null,
      airportIdent: data.airportIdent || null,
      airportName: data.airportName || null,
      tags: tags,
      categories: categories,
      privateRentals: tags.indexOf(RENTAL_PRIVATE_TAG) !== -1,
      thumbnailPhotoPath: data.thumbnailPhotoPath || null
    });
  }
}
